/*************************************************************************
* Copyright (c) 2025 Omnineura AI Technologies Pvt Limited.
* All rights reserved.
* This code is licensed under the 'Omnineura AI License' found in the LICENSE file.
* Unauthorized copying, sharing, or use of this code is prohibited.
* The intellectual and technical concepts are proprietary to Omnineura AI Technologies Pvt Limited.
* Author: surya@Omnineura.ai
* Date: 11-09-2025
*************************************************************************/

// reducers/chatSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { createNewChatSessionApi, getAllChatSessionsApi, deleteChatSessionApi, getChatSessionMessagesApi, sendChatMessageApi } from "../services/api";

// 🔹 AsyncThunk
export const createNewChatSession = createAsyncThunk(
  "chat/createSession",
  async (payload, { rejectWithValue }) => {
    try {

      const response = await createNewChatSessionApi(payload);
      return response; // response is { success, data }
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

export const getAllChatSessions = createAsyncThunk(
  "chat/getAllChatSession", async (__, { rejectWithValue }) => {

    try {
      const response = await getAllChatSessionsApi();
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  })

export const deleteChatSession = createAsyncThunk(
  "chat/deleteChatSession", async (sessionId, { rejectWithValue }) => {

    try {

      const response = await deleteChatSessionApi(sessionId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }

  }
)

export const getChatSessionMessages = createAsyncThunk(
  "chat/getChatSessionMessages", async (sessionId, { rejectWithValue }) => {

    try {

      const response = await getChatSessionMessagesApi(sessionId)
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }

  }
)

export const sendChatMessage = createAsyncThunk(
  "chat/sendChatMessage",
  async ({ session_id, message }, { rejectWithValue }) => {
    try {
      const response = await sendChatMessageApi({ session_id, message });
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// 🔹 Slice
const chatReducer = createSlice({
  name: "chat",
  initialState: {
    session: null,       // current session
    sessionMessages: [],// messages of current session
    sessions: [],        // all sessions
    totalSessions: 0,            // total sessions count
    loading: false,
    loadingSessions: false,
    error: null,
  },
  reducers: {
    clearSession: (state) => {
      state.session = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create session
      .addCase(createNewChatSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createNewChatSession.fulfilled, (state, action) => {
        state.loading = false;
        // state.session = action.payload.data; // backend gives {session_id, title, message}
        // state.sessions.push(action.payload.data); // ✅ optional: keep a list of sessions
        const newSession = action.payload.data; // {session_id, title, message}
        state.session = newSession;              // set active session
        state.sessions = [newSession, ...state.sessions]; // prepend to list
      })
      .addCase(createNewChatSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get sessions
      .addCase(getAllChatSessions.pending, (state) => {
        state.loadingSessions = true;
        state.error = null;
      })
      .addCase(getAllChatSessions.fulfilled, (state, action) => {
        state.loadingSessions = false;
        state.sessions = action.payload.data.sessions;
        state.totalSessions = action.payload.data.total;
       
      })
      .addCase(getAllChatSessions.rejected, (state, action) => {
        state.loadingSessions = false;
        state.error = action.payload;
      })

      //delete chat session
      .addCase(deleteChatSession.fulfilled, (state, action) => {
        const sessions = state.sessions.filter(session => session.session_id !== action.payload.data.sesssion_id);
        state.sessions = sessions;
        // If the deleted session was the current one, clear it
      })

      //get messages of a particular session
      .addCase(getChatSessionMessages.fulfilled, (state, action) => {
        state.session = action.payload.data.session;
        // state.session = action.payload.data;
        // Update session messages in Redux
        state.sessionMessages = {
          ...state.sessionMessages,
          [action.payload.data.session.session_id]: action.payload.data.messages.map(msg => ({
            id: msg.message_id,
            content: msg.assistant || msg.user,
            type: msg.user === "user" ? "user" : "assistant",
            timestamp: msg.timestamp
          }))
        };

      })

  },
});

export const { clearSession } = chatReducer.actions;
export default chatReducer.reducer;
